import { AfterViewInit, Component, computed, ElementRef, inject, OnDestroy, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { OverlayRef } from '@angular/cdk/overlay';
import { PhIconWrapperComponent } from '../../../../core/components/ph-icon-wrapper/ph-icon-wrapper.component';
import { PlanogramCanvasComponent } from '../../../../core/components/planogram-canvas/planogram-canvas.component';
import { IPlanogram } from '../../../models/planogram';
import { PlanogramActivity } from '../../../services/planogram-activity.service';
import {
	PLANOGRAM_CANVAS_WIDGET_DATA,
	PLANOGRAM_CANVAS_WIDGET_REF,
	PlanogramCanvasWidgetData,
	PlanogramCanvasWidgetRef
} from './planogram-canvas-widget.tokens';
import { LAYOUT_PADDING_PX, MIN_PANEL_HEIGHT_PX, MIN_PANEL_WIDTH_PX, PlanogramCanvasOverlayService } from '../../../services/planogram-canvas-overlay.service';
import { PlanogramModes } from '../../../services/planogram-modes.service';
import { DetailedElementWidgetOverlayService } from '../../../services/detailed-element-widget-overlay.service';
import { clampOverlayPosition } from '../../../services/overlay-viewport.util';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

interface ResizeState {
	resizing: boolean;
	anchorLeft: number;
	anchorTop: number;
	startClientX: number;
	startClientY: number;
	startWidth: number;
	startHeight: number;
	minWidth: number;
	minHeight: number;
}

@Component({
	selector: 'planogram-canvas-widget',
	templateUrl: './planogram-canvas-widget.component.html',
	styleUrls: ['./planogram-canvas-widget.component.scss'],
	host: {
		'[class.active]': 'activity.planogram()?.main_stand === planogram.main_stand',
		'[class.dragging]': 'dragging()',
		'(pointerdown)': 'onHostPointerDown()'
	},
	imports: [PhIconWrapperComponent, PlanogramCanvasComponent, MatTooltipModule, TranslateModule]
})
export class PlanogramCanvasWidgetComponent implements AfterViewInit, OnDestroy {

	private readonly widgetRef: PlanogramCanvasWidgetRef = inject(PLANOGRAM_CANVAS_WIDGET_REF);
	readonly inputData: PlanogramCanvasWidgetData = inject(PLANOGRAM_CANVAS_WIDGET_DATA);

	readonly dragging: WritableSignal<boolean> = signal(false);
	readonly planogram: IPlanogram = this.inputData.planogram;
	readonly overlayRef: OverlayRef = this.inputData.overlayRef;
	readonly activity: PlanogramActivity = inject(PlanogramActivity);
	readonly modes: PlanogramModes = inject(PlanogramModes);
	readonly detailedElementWidgetOverlayService: DetailedElementWidgetOverlayService = inject(DetailedElementWidgetOverlayService);
	readonly overlayService: PlanogramCanvasOverlayService = inject(PlanogramCanvasOverlayService);
	readonly isPinned: Signal<boolean> = computed(() => this.overlayService.pinnedPlanogramIds().includes(this.planogram.main_stand));

	@ViewChild('headerContainer') headerContainer: ElementRef<HTMLElement>;
	@ViewChild('resizeHandle') resizeHandle: ElementRef<HTMLElement>;
	@ViewChild(PlanogramCanvasComponent) planogramCanvas: PlanogramCanvasComponent;

	private hostListenersAbortController: AbortController | null = null;
	private dragOffsetX: number = 0;
	private dragOffsetY: number = 0;
	private resizeState: ResizeState = {
		resizing: false,
		anchorLeft: 0,
		anchorTop: 0,
		startClientX: 0,
		startClientY: 0,
		startWidth: 0,
		startHeight: 0,
		minWidth: MIN_PANEL_WIDTH_PX,
		minHeight: MIN_PANEL_HEIGHT_PX
	};

	ngAfterViewInit(): void {
		this.hostListenersAbortController = new AbortController();
		this.initDrag();
		this.initResize();
	}

	getCanvasZoom(): number {
		return this.planogramCanvas?.zoom() ?? 100;
	}

	zoomApply(value: number): void {
		this.planogramCanvas?.zoomApply(value);
	}

	getCanvasImage(): string {
		return this.planogramCanvas?.getCanvasImage() ?? '';
	}

	ngOnDestroy(): void {
		this.dragging.set(false);
		this.hostListenersAbortController?.abort();
		this.hostListenersAbortController = null;
	}

	onHostPointerDown(): void {
		this.widgetRef.focus();
		this.activity.setPlanogram(this.planogram);
	}

	togglePin(): void {
		if (this.modes.layoutRulesLocked()) return;

		this.overlayService.setPinned(this.planogram.main_stand, !this.isPinned());
	}

	collapsePanel(): void {
		if (this.modes.layoutRulesLocked()) return;

		this.detailedElementWidgetOverlayService.closeAllForPlanogram(this.planogram);
		this.activity.clearPlanogramSelection(this.planogram.stand);
		this.overlayService.close(this.overlayRef);
	}

	expandToContainer(event: MouseEvent): void {
		if (this.modes.layoutRulesLocked()) return;
		if (this.isPinned()) return;
		if ((event.target as HTMLElement).closest('[data-no-drag]')) return;

		const containerRect: DOMRect = this.inputData.hostContainer.getBoundingClientRect();
		if (!containerRect.width || !containerRect.height) return;

		const overlayEl: HTMLElement = this.overlayRef.overlayElement as HTMLElement;

		overlayEl.style.position = 'fixed';
		overlayEl.style.left = `${containerRect.left + LAYOUT_PADDING_PX}px`;
		overlayEl.style.top = `${containerRect.top + LAYOUT_PADDING_PX}px`;
		overlayEl.style.width = `${containerRect.width - LAYOUT_PADDING_PX * 2}px`;
		overlayEl.style.height = `${containerRect.height - LAYOUT_PADDING_PX * 2}px`;
		overlayEl.style.right = 'auto';
		overlayEl.style.bottom = 'auto';
		overlayEl.style.transform = 'none';

		this.widgetRef.focus();
	}

	private getPointerContentX(event: PointerEvent, container: HTMLElement): number {
		return event.clientX - container.getBoundingClientRect().left + container.scrollLeft;
	}

	private getPinnedMaxLeft(overlayEl: HTMLElement): number {
		return Math.max(
			LAYOUT_PADDING_PX,
			this.overlayService.getPinnedDragMaxLeft(overlayEl.offsetWidth)
		);
	}

	private setOverlayPosition(position: 'fixed' | 'absolute' = 'fixed'): DOMRect {
		const overlayEl: HTMLElement = this.overlayRef.overlayElement as HTMLElement;
		const rect: DOMRect = overlayEl.getBoundingClientRect();
		const hostContainer: HTMLElement = this.inputData.hostContainer;
		const hostRect: DOMRect = hostContainer.getBoundingClientRect();

		overlayEl.style.position = position;
		overlayEl.style.left = position === 'absolute'
			? `${rect.left - hostRect.left}px`
			: `${rect.left}px`;
		overlayEl.style.top = position === 'absolute'
			? `${rect.top - hostRect.top}px`
			: `${rect.top}px`;
		overlayEl.style.right = 'auto';
		overlayEl.style.bottom = 'auto';
		overlayEl.style.transform = 'none';

		return rect;
	}

	private initDrag(): void {
		if (!this.headerContainer || !this.hostListenersAbortController) return;

		const signal: AbortSignal = this.hostListenersAbortController.signal;
		const header: HTMLElement = this.headerContainer.nativeElement;

		header.addEventListener('pointerdown', (event: PointerEvent) => {
			if (event.button !== 0) return;
			if ((event.target as HTMLElement).closest('[data-no-drag]')) return;

			event.preventDefault();

			const overlayEl: HTMLElement = this.overlayRef.overlayElement as HTMLElement;

			if (this.isPinned()) {
				const container: HTMLElement = this.inputData.hostContainer;
				const contentX: number = this.getPointerContentX(event, container);
				this.dragOffsetX = contentX - overlayEl.offsetLeft;
				this.dragOffsetY = event.clientY - container.getBoundingClientRect().top + container.scrollTop - overlayEl.offsetTop;
				this.widgetRef.focus();
				this.overlayService.beginPinnedDrag();
			} else {
				const rect: DOMRect = this.setOverlayPosition();
				this.dragOffsetX = event.clientX - rect.left;
				this.dragOffsetY = event.clientY - rect.top;
			}

			this.dragging.set(true);
			header.setPointerCapture(event.pointerId);
		}, { signal });

		header.addEventListener('pointermove', (event: PointerEvent) => {
			if (!this.dragging()) return;

			const overlayEl: HTMLElement = this.overlayRef.overlayElement as HTMLElement;

			if (this.isPinned()) {
				const container: HTMLElement = this.inputData.hostContainer;
				const minLeft: number = LAYOUT_PADDING_PX;
				const maxLeft: number = this.getPinnedMaxLeft(overlayEl);
				const contentX: number = this.getPointerContentX(event, container);
				const left: number = Math.min(
					Math.max(contentX - this.dragOffsetX, minLeft),
					maxLeft
				);

				overlayEl.style.position = 'absolute';
				overlayEl.style.left = `${left}px`;
				this.overlayService.movePinnedSlot(this.planogram.main_stand, left + overlayEl.offsetWidth / 2);
			} else {
				const nextLeft: number = event.clientX - this.dragOffsetX;
				const nextTop: number = event.clientY - this.dragOffsetY;
				const clamped = clampOverlayPosition(nextLeft, nextTop, overlayEl);

				overlayEl.style.left = `${clamped.left}px`;
				overlayEl.style.top = `${clamped.top}px`;
			}
		}, { signal });

		header.addEventListener('pointerup', (event: PointerEvent) => {
			if (!this.dragging()) return;

			if (this.isPinned()) {
				this.overlayService.commitPinnedDrag();
			}

			this.dragging.set(false);
			header.releasePointerCapture(event.pointerId);
		}, { signal });

		header.addEventListener('pointercancel', () => {
			if (this.dragging() && this.isPinned()) {
				this.overlayService.cancelPinnedDrag();
			}

			this.dragging.set(false);
		}, { signal });

		header.style.touchAction = 'none';
		header.style.cursor = 'grab';
	}

	private initResize(): void {
		if (!this.resizeHandle || !this.hostListenersAbortController) return;

		const signal: AbortSignal = this.hostListenersAbortController.signal;

		const onPointerDown = (event: PointerEvent): void => {
			if (this.isPinned() || event.button !== 0) return;

			event.preventDefault();
			event.stopPropagation();

			const rect: DOMRect = this.setOverlayPosition();

			this.resizeState.resizing = true;
			this.resizeState.anchorLeft = rect.left;
			this.resizeState.anchorTop = rect.top;
			this.resizeState.startClientX = event.clientX;
			this.resizeState.startClientY = event.clientY;
			this.resizeState.startWidth = rect.width;
			this.resizeState.startHeight = rect.height;

			this.resizeHandle.nativeElement.setPointerCapture(event.pointerId);
		};

		const onPointerMove = (event: PointerEvent): void => {
			if (!this.resizeState.resizing) return;

			const overlayEl: HTMLElement = this.overlayRef.overlayElement as HTMLElement;
			const {
				anchorLeft,
				anchorTop,
				startClientX,
				startClientY,
				startWidth,
				startHeight,
				minWidth,
				minHeight
			} = this.resizeState;
			const dx: number = event.clientX - startClientX;
			const dy: number = event.clientY - startClientY;

			overlayEl.style.width = `${Math.min(window.innerWidth - anchorLeft, Math.max(minWidth, startWidth + dx))}px`;
			overlayEl.style.height = `${Math.min(window.innerHeight - anchorTop, Math.max(minHeight, startHeight + dy))}px`;
		};

		const onPointerUp = (event: PointerEvent): void => {
			if (!event.isPrimary || !this.resizeState.resizing) return;

			this.resizeState.resizing = false;
			this.resizeHandle.nativeElement.releasePointerCapture(event.pointerId);
		};

		this.resizeHandle.nativeElement.style.touchAction = 'none';
		this.resizeHandle.nativeElement.addEventListener('pointerdown', onPointerDown, { signal });
		this.resizeHandle.nativeElement.addEventListener('pointermove', onPointerMove, { signal });
		this.resizeHandle.nativeElement.addEventListener('pointerup', onPointerUp, { signal });
		this.resizeHandle.nativeElement.addEventListener('pointercancel', onPointerUp, { signal });
	}
}
